/* Photo sequence: no WebGL, canvas, animation loop or third-party runtime. */
(() => {
  if (customElements.get('shopsta-image-sequence')) return;
  customElements.define('shopsta-image-sequence', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      const frames = [...this.querySelectorAll('[data-sequence-frame]')];
      if (frames.length < 2) return;
      const images = frames.map(frame => frame.querySelector('img'));
      const buttons = [...this.querySelectorAll('[data-sequence-index]')];
      const stage = this.querySelector('[data-sequence-stage]');
      const surface = this.closest('section') || this;
      surface.setAttribute('data-sequence-swipe-surface', '');
      this.swipeSurface = surface;
      const count = this.querySelector('[data-sequence-count]');
      const caption = this.querySelector('[data-sequence-caption]');
      let index = 0, requested = 0, revision = 0, drag = null;
      const loading = new Map();
      const load = i => {
        if (!loading.has(i)) {
          const img = images[i];
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          if (img.dataset.src) img.src = img.dataset.src;
          loading.set(i, img.decode().then(() => true).catch(() => { loading.delete(i); return false; }));
        }
        return loading.get(i);
      };
      const warm = () => images.forEach((_, i) => load(i));
      const show = async value => {
        requested = (value + frames.length) % frames.length;
        const target = requested, token = ++revision;
        if (!await load(target) || token !== revision || options.signal.aborted) return;
        frames[index].hidden = true;
        frames[target].hidden = false;
        index = target;
        buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
        count.textContent = `${index + 1} / ${frames.length}`;
        caption.textContent = frames[index].dataset.caption;
        this.dataset.index = String(index);
      };
      this.querySelectorAll('[data-sequence-controls]').forEach(el => el.hidden = false);
      stage.tabIndex = 0;
      this.dataset.ready = 'true';
      buttons.forEach((button, i) => button.addEventListener('click', () => show(i), options));
      this.querySelector('[data-sequence-prev]').addEventListener('click', () => show(requested - 1), options);
      this.querySelector('[data-sequence-next]').addEventListener('click', () => show(requested + 1), options);
      stage.addEventListener('keydown', event => {
        const target = {ArrowLeft: requested - 1, ArrowRight: requested + 1, Home: 0, End: frames.length - 1}[event.key];
        if (target === undefined) return;
        event.preventDefault(); show(target);
      }, options);
      const end = () => {
        const pointerId = drag?.id;
        drag = null;
        surface.classList.remove('is-dragging');
        if (pointerId !== undefined && surface.hasPointerCapture(pointerId)) surface.releasePointerCapture(pointerId);
      };
      surface.addEventListener('pointerdown', event => {
        if (!event.isPrimary || event.button !== 0) return;
        if (event.target.closest('button, a, input, select, textarea, summary, [contenteditable=true]')) return;
        drag = {id: event.pointerId, x: event.clientX, y: event.clientY, start: requested, active: false};
        surface.setPointerCapture(event.pointerId);
        warm();
      }, options);
      surface.addEventListener('pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.active) {
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { end(); return; }
          if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
          drag.active = true;
          surface.classList.add('is-dragging');
        }
      }, options);
      surface.addEventListener('pointerup', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        const start = drag.start;
        // One intentional horizontal gesture advances exactly one photograph.
        const swipe = Math.abs(dx) >= 24 && Math.abs(dx) > Math.abs(dy);
        end();
        if (swipe) show(start + (dx < 0 ? 1 : -1));
      }, options);
      surface.addEventListener('pointercancel', end, options);
      surface.addEventListener('lostpointercapture', end, options);
      this.addEventListener('focusin', warm, {...options, once:true});
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { warm(); this.observer.disconnect(); }
        }, {rootMargin: '300px'});
        this.observer.observe(this);
      }
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.abort = null;
      this.swipeSurface?.removeAttribute('data-sequence-swipe-surface');
      this.swipeSurface?.classList.remove('is-dragging');
    }
  });
})();
